import React, { useContext, useState } from 'react';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { Company } from '../types';
import { FRETE_LABELS, FRETE_MODOS_SELECIONAVEIS } from '../constants';
import { StyledCard, CardTitle, FormGrid, StyledFormGroup, TooltipWrapper, InfoIcon } from '../cardStyles';
import {
  HeaderRow,
  EditToggleButton,
  SaveCancelBar,
  SmallOutlineButton,
  SmallSolidButton,
  EditedNotice,
} from './FreteSection.style';

interface Props {
  invoice: any;
  invoiceId: number;
  companies: Company[];
  readOnly?: boolean;
  onSaved: () => void;
}

const LabelWithInfo = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

const FreteSection: React.FC<Props> = ({ invoice, invoiceId, companies, readOnly, onSaved }) => {
  const { setFeedback } = useContext(FeedbackContext);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalidade, setModalidade] = useState('');
  const [transportadoraId, setTransportadoraId] = useState('');

  const modalidadeAtual =
    invoice?.modalidadeFrete ??
    (invoice?.Order?.ShippingType?.name && invoice.Order.ShippingType.name.toUpperCase() in FRETE_LABELS
      ? invoice.Order.ShippingType.name.toUpperCase()
      : null);
  const transportadoraAtual = invoice?.CompanyTransportadora ?? null;
  const modalidadePedido: string | null = invoice?.Order?.ShippingType?.name
    ? String(invoice.Order.ShippingType.name).trim().toUpperCase()
    : null;
  const fretePersonalizado =
    Boolean(invoice?.companyTransportadoraId) ||
    Boolean(invoice?.modalidadeFrete && invoice.modalidadeFrete !== modalidadePedido);

  const beginEdit = () => {
    setModalidade(invoice?.modalidadeFrete ?? '');
    setTransportadoraId(invoice?.companyTransportadoraId ? String(invoice.companyTransportadoraId) : '');
    setEditing(true);
  };

  const handleSalvar = async () => {
    setSaving(true);
    try {
      await ApiService.fiscal.updateInvoice(invoiceId, {
        modalidadeFrete: modalidade || undefined,
        companyTransportadoraId: transportadoraId ? Number(transportadoraId) : undefined,
      });
      setEditing(false);
      setFeedback({ message: 'Frete da nota atualizado.', isOpen: true, statusOk: true });
      onSaved();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      setFeedback({
        message: Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao salvar o frete da nota.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <StyledCard>
        <CardTitle>Frete</CardTitle>
        <FormGrid cols={2}>
          <StyledFormGroup>
            <LabelWithInfo label="Modalidade" tooltip="Modalidade de frete da nota" />
            <select value={modalidade} onChange={(e) => setModalidade(e.target.value)}>
              <option value="">Herdar do pedido</option>
              {FRETE_MODOS_SELECIONAVEIS.map((key) => (
                <option key={key} value={key}>
                  {FRETE_LABELS[key]}
                </option>
              ))}
            </select>
          </StyledFormGroup>
          <StyledFormGroup>
            <LabelWithInfo label="Transportadora" tooltip="Transportadora vinculada à nota" />
            <select value={transportadoraId} onChange={(e) => setTransportadoraId(e.target.value)}>
              <option value="">Nenhuma</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName}
                </option>
              ))}
            </select>
          </StyledFormGroup>
        </FormGrid>
        <SaveCancelBar>
          <SmallOutlineButton type="button" onClick={() => setEditing(false)} disabled={saving}>
            Cancelar
          </SmallOutlineButton>
          <SmallSolidButton type="button" onClick={handleSalvar} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar frete'}
          </SmallSolidButton>
        </SaveCancelBar>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <HeaderRow>
        <CardTitle style={{ paddingBottom: 0 }}>Frete</CardTitle>
        {!readOnly && <EditToggleButton onClick={beginEdit}>Editar frete</EditToggleButton>}
      </HeaderRow>
      {fretePersonalizado && (
        <EditedNotice>Frete personalizado nesta nota — diferente do pedido de origem.</EditedNotice>
      )}
      <FormGrid cols={2}>
        <StyledFormGroup>
          <LabelWithInfo label="Modalidade" tooltip="Modalidade de frete definida na nota" />
          <input
            type="text"
            readOnly
            value={modalidadeAtual ? FRETE_LABELS[modalidadeAtual] || modalidadeAtual : '—'}
          />
        </StyledFormGroup>
        <StyledFormGroup>
          <LabelWithInfo label="Transportadora" tooltip="Transportadora vinculada à nota" />
          <input type="text" readOnly value={transportadoraAtual?.companyName || '—'} />
        </StyledFormGroup>
      </FormGrid>
    </StyledCard>
  );
};

export default FreteSection;
